import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Search } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import ContactForm from '../components/ContactForm';

const Contact = () => {
    const { t } = useTranslation();
    useSEO({
        title: 'Contacto B2B | Estructura Comercial Laz Metals',
        description: 'Vende tus excedentes industriales, estructura liquidaciones de chatarra o cotiza suministro continuo. Operaciones logísticas pesadas e ingeniería.',
    });

    return (
        <div className="w-full">
            {/* HEADER CONTACT */}
            <section className="min-h-[80dvh] lg:min-h-[60vh] py-16 lg:py-32 massive-container flex flex-col items-center justify-center relative overflow-hidden mt-6 group">
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[15s] group-hover:scale-105"
                    style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)' }}
                />
                <div className="absolute inset-0 bg-accent/85 backdrop-blur-[2px]" />

                <div className="container mx-auto px-4 md:px-12 max-w-5xl text-left md:text-center relative z-10 text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="section-tag-orange mb-4 text-orange-400 border-orange-400/30 md:mx-auto inline-flex">{t('contactPage.tag')}</div>
                        <h1 className="text-3xl lg:text-6xl font-heading font-medium mb-6 leading-tight text-white drop-shadow-md">
                            {t('contactPage.title')}
                        </h1>
                        <p className="text-gray-300 font-body text-lg leading-relaxed mb-0 max-w-3xl mx-auto">
                            {t('contactPage.desc')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* DUAL LAYOUT CONTACT & FORM */}
            <section className="py-24">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">

                        {/* Left Column - Contact Info */}
                        <div className="lg:col-span-2">
                            <h3 className="text-2xl font-heading font-medium text-accent mb-8 border-l-4 border-primary pl-4">
                                {t('contactPage.infoTitle')}
                            </h3>

                            <ul className="space-y-8 font-body">
                                <li className="flex gap-4">
                                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-primary shrink-0 border border-gray-100 shadow-sm">
                                        <Search size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 mb-1">{t('contactPage.legalEntity')}</h4>
                                        <p className="text-gray-500 text-sm">Laz Metals LLC</p>
                                    </div>
                                </li>

                                <li className="flex gap-4">
                                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-primary shrink-0 border border-gray-100 shadow-sm">
                                        <Phone size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 mb-1">{t('contactPage.directPhone')}</h4>
                                        <p className="text-gray-500 text-sm">+1 929 290 2022</p>
                                    </div>
                                </li>

                                <li className="flex gap-4">
                                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-primary shrink-0 border border-gray-100 shadow-sm">
                                        <Mail size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 mb-1">{t('contactPage.corporateMail')}</h4>
                                        <p className="text-gray-500 text-sm">Luislazo@lazmetals.com</p>
                                    </div>
                                </li>

                                <li className="flex gap-4">
                                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-primary shrink-0 border border-gray-100 shadow-sm">
                                        <MapPin size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 mb-1">{t('contactPage.corporateAddress')}</h4>
                                        <p className="text-gray-500 text-sm">3625 NW 82nd Ave, Suite 318, Doral, FL 33166</p>
                                    </div>
                                </li>
                            </ul>

                            <div className="mt-12 p-6 bg-bg-secondary rounded-card border border-gray-200">
                                <p className="text-sm text-gray-600 font-medium">{t('contactPage.hoursTitle')}</p>
                                <p className="text-sm text-gray-500">{t('contactPage.hoursDesc')}</p>
                            </div>
                        </div>

                        {/* Right Column - B2B Form */}
                        <div className="lg:col-span-3">
                            <div className="bg-white p-8 md:p-12 rounded-block shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 relative">
                                <h3 className="text-2xl font-heading font-medium text-accent mb-8">
                                    {t('contactPage.formTitle')}
                                </h3>

                                <ContactForm />
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};


export default Contact;

